import React, { useState, useEffect, useRef } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useLocation,
  Navigate,
} from "react-router-dom";
import moment from "moment";
import useApi from "../../hooks/useApi";
import useApp from "../../hooks/useApp";
import useReload from "../../hooks/useReload";
import DatePicker from "react-datepicker";
import { NavigateBackButton } from "../../widgets";
import ClientPolicyRequirement from "./ClientPolicyRequirement";
import PolicyItemStatus from "../../widgets/PolicyItemStatus";
import PolicyUnderwriteForm from "./PolicyUnderwriteForm";
import ClientPolicyAttachment from "./ClientPolicyAttachment";
import ClientPolicyAttachmentForm from "./ClientPolicyAttachmentForm";
import ClientPolicyProcessing from "./ClientPolicyProcessing";

import Select from "react-select";
import {
  Accordion,
  Button,
  Modal,
  Container,
  Form,
  InputGroup,
  Row,
  Col,
  Tab,
  Tabs,
} from "react-bootstrap";
import Swal from "sweetalert2";

const PolicyItemView = () => {
  const navigate = useNavigate();
  const { tab = "policy" } = useParams();
  const { itemid = null } = useParams();

  const [policy, setPolicy] = useState({});
  const [renewPolicy, setRenewPolicy] = useState({});
  const [renewPolicyNote, setRenewPolicyNote] = useState();
  const { api, abortSignal, auth } = useApi();
  const { showAlert } = useApp();
  const { reload } = useReload();

  const companyRef = useRef();
  const branchRef = useRef();
  const policyNumberRef = useRef();
  const premiumRef = useRef();
  const renewalPremiumRef = useRef();

  const [showRenew, setShowRenew] = useState(false);
  const handleCloseRenew = () => setShowRenew(false);
  const handleShowRenew = () => setShowRenew(true);

  const [showRenewNote, setShowRenewNote] = useState(false);
  const handleCloseRenewNote = () => setShowRenewNote(false);
  const handleShowRenewNote = () => setShowRenewNote(true);

  const [showProcess, setShowProcess] = useState(false);
  const handleCloseProcess = () => setShowProcess(false);
  const handleShowProcess = () => setShowProcess(true);

  const [underwritePolicy, setUnderwritePolicy] = useState({});
  const [dropDowns, setDropDowns] = useState({ branch: [] });
  const { toggleReload } = useReload();
  const [startDate, setStartDate] = useState(new Date());

  const getPolicy = async () => {
    try {
      if (itemid) {
        const { data = {} } = await api.read(
          `/policies/policy-items/${itemid}`
        );
        setPolicy(data);

        const effectiveDate = moment(data.effectiveDate || new Date())
          .add(data?.duration || 12, "months")
          .format("YYYY-MM-DD");

        setRenewPolicy({
          duration: data.duration,
          CompanyId: data.CompanyId,
          BranchId: data.BranchId,
          ProductId: data.ProductId,
          effectiveDate: effectiveDate,
          expires: moment(effectiveDate || new Date())
            .add(data?.duration || 12, "months")
            .subtract(1, "day")
            .format("YYYY-MM-DD"),
          premium: data.renewalPremium || data.premium,
          status: "renewal",
        });

        setRenewPolicyNote({
          ...renewPolicyNote,
          ClientPolicyId: data?.ClientPolicyId,
          premium: data?.premium,
          policyCurrency: data?.policyCurrency || "GHS",
        });
      }
    } catch (error) {}
  };

  const getDropDowns = async (models) => {
    try {
      const { data } = await api.list(`/dropdowns`, models);
      return data;
    } catch (error) {
      console.error(error);
    }
  };

  const companyDropDown = async () => {
    try {
      const data = await getDropDowns(["company"]);

      if (
        data &&
        (typeof renewPolicy?.CompanyId === "string" ||
          renewPolicy?.CompanyId === "undefined")
      )
        setRenewPolicy({
          ...renewPolicy,
          CompanyId: data.company.find(
            (item) => item.value == renewPolicy.CompanyId
          ),
        });
      setDropDowns({ ...dropDowns, ...data });
    } catch (error) {}
  };

  const branchDropDown = async () => {
    try {
      const data = await getDropDowns([
        {
          branch: { CompanyId: renewPolicy.CompanyId.value },
        },
      ]);
      if (data && typeof renewPolicy.BranchId === "string")
        setRenewPolicy({
          ...renewPolicy,
          BranchId: data.branch.find(
            (item) => item.value == renewPolicy.BranchId
          ),
        });
      setDropDowns({ ...dropDowns, ...data });
    } catch (error) {}
  };

  useEffect(() => {
    showRenew && premiumRef.current.select();
  }, [showRenew]);

  useEffect(() => {
    showRenewNote && renewalPremiumRef.current.select();
  }, [showRenewNote]);

  useEffect(() => {
    getPolicy();
  }, [itemid, auth, reload]);

  useEffect(() => {
    companyDropDown();
  }, [policy]);

  useEffect(() => {
    branchDropDown();
  }, [renewPolicy?.CompanyId]);

  useEffect(() => {
    setRenewPolicy({
      ...renewPolicy,
      expires: moment(renewPolicy?.effectiveDate || new Date())
        .add(renewPolicy?.duration || 12, "months")
        .subtract(1, "day")
        .format("YYYY-MM-DD"),
    });
  }, [renewPolicy?.duration, renewPolicy?.effectiveDate]);

  const handleSaveRenewalNote = async () => {
    if (
      !(renewPolicyNote?.renewalPremium && renewPolicyNote?.renewalPremium > 0)
    ) {
      showAlert("Renewal Premium is invalid.", "error");
      renewalPremiumRef.current.focus();
      return;
    }

    Swal.fire({
      title: "Policy Renewal Notice",
      text: "Are sure you want to set renewal notice for this policy?",
      icon: "info",
      allowOutsideClick: false,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes",
      showCancelButton: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const response = await api.update(
          `/client-policies/${renewPolicyNote.ClientPolicyId}/renewal-notice`,
          renewPolicyNote
        );

        if (response.ok) {
          showAlert("Policy renewal notice saved", "success");
        }
      }
      handleCloseRenewNote();
    });
  };

  const handleSaveRenewal = async () => {
    if (!(renewPolicy?.premium && renewPolicy?.premium > 0)) {
      showAlert("Renewal Premium is invalid.", "error");
      return;
    }

    Swal.fire({
      title: "Renew Policy",
      text: "Are sure you want to renew this policy?",
      icon: "info",
      allowOutsideClick: false,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes",
      showCancelButton: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const data = {
          ...renewPolicy,
          BranchId: renewPolicy?.BranchId?.value || null,
          CompanyId: renewPolicy?.CompanyId?.value || null,
        };
        const response = await api.add(`/policy-items/${itemid}/renew`, data);

        if (response.ok) {
          const renewedPolicy = response.data;
          showAlert("Policy renewed", "success");
          navigate(
            `/policies/${renewedPolicy.PolicyId}/item/${renewedPolicy.id}`
          );
        } else {
          showAlert("Policy renewal failed. Please try again", "error");
        }
        handleCloseRenew();
      } else {
        handleCloseRenew();
      }
    });
  };

  return (
    <section>
      <Tabs
        defaultActiveKey={tab}
        id="client-policy-view"
        className="mb-3"
        onSelect={(selectedTab) =>
          selectedTab === "claim"
            ? !(tab === "claim") && navigate("claim")
            : !(tab === "policy") && navigate(-1)
        }
      >
        <Tab eventKey="policy" title="POLICY VIEW">
          {policy && (
            <div className="d-md-flex align-items-baseline mt-3">
              <h2 className="me-2">
                <PolicyItemStatus status={policy?.status} />
              </h2>

              {["expiring", "expired", "awaiting"].includes(policy?.status) && (
                <>
                  <>
                    <Button
                      variant="outline-primary me-2"
                      size="xs"
                      onClick={handleShowRenewNote}
                    >
                      <i className="bi bi-alarm fs-5 me-2" />
                      Renewal Notice
                    </Button>
                    <Modal
                      show={showRenewNote}
                      onHide={handleCloseRenewNote}
                      backdrop="static"
                    >
                      <Modal.Header closeButton>
                        <Modal.Title>Policy Renewal Notice</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        <Form>
                          <Form.Group as={Col} controlId="formGridCity">
                            <Form.Label className="text-primary">
                              Current Premium
                            </Form.Label>
                            <Form.Control
                              defaultValue={
                                renewPolicyNote?.policyCurrency +
                                " " +
                                Number.parseFloat(
                                  renewPolicyNote?.premium
                                ).toLocaleString("en-US")
                              }
                              readOnly
                            />
                          </Form.Group>
                          <Form.Group className="mt-3">
                            <Row>
                              <Col>
                                <Form.Label className="text-primary">
                                  Renewal Premium
                                </Form.Label>
                              </Col>
                              {renewPolicyNote?.premium && (
                                <Col>
                                  <div className="form-check form-switch">
                                    <Form.Label className="text-primary">
                                      Use Last Premium
                                    </Form.Label>

                                    <Form.Control
                                      id="copy-last-premium"
                                      className="form-check-input me-3"
                                      type="checkbox"
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setRenewPolicyNote({
                                            ...renewPolicyNote,
                                            renewalPremium:
                                              renewPolicyNote?.premium,
                                          });
                                        } else {
                                          setRenewPolicyNote({
                                            ...renewPolicyNote,
                                            renewalPremium: 0,
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                </Col>
                              )}
                            </Row>

                            <InputGroup className="mb-3">
                              <InputGroup.Text>
                                {renewPolicyNote?.policyCurrency || "GHS"}
                              </InputGroup.Text>
                              <Form.Control
                                aria-label="Amount (to the nearest value)"
                                ref={renewalPremiumRef}
                                id="premium"
                                type="number"
                                min={0}
                                step={"0.01"}
                                value={renewPolicyNote?.renewalPremium || 0.0}
                                onChange={(e) =>
                                  setRenewPolicyNote({
                                    ...renewPolicyNote,
                                    renewalPremium: e.target.value,
                                  })
                                }
                                onFocus={() =>
                                  renewalPremiumRef.current.select()
                                }
                              />
                              <InputGroup.Text className="fw-bold">
                                {Number.parseFloat(
                                  renewPolicyNote?.renewalPremium
                                ).toLocaleString("en-US")}
                              </InputGroup.Text>
                            </InputGroup>
                          </Form.Group>
                          <Form.Group
                            className="mb-3"
                            controlId="formGridAddress2"
                          >
                            <Form.Label className="text-primary">
                              Remarks
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              placeholder="Leave a comment here"
                              rows={10}
                              value={renewPolicyNote?.comments}
                              onChange={(e) =>
                                setRenewPolicyNote({
                                  ...renewPolicyNote,
                                  comments: e.target.value,
                                })
                              }
                            />
                          </Form.Group>
                        </Form>
                      </Modal.Body>
                      <Modal.Footer>
                        <Button
                          variant="secondary"
                          onClick={handleCloseRenewNote}
                        >
                          Close
                        </Button>
                        <Button
                          variant="primary"
                          onClick={handleSaveRenewalNote}
                        >
                          Submit
                        </Button>
                      </Modal.Footer>
                    </Modal>
                  </>
                  <>
                    {policy?.RenewedId ? (
                      <Button
                        variant="outline-primary me-2"
                        size="xs"
                        onClick={() =>
                          navigate(
                            `/policies/${policy?.PolicyId}/item/${policy?.RenewedId}`
                          )
                        }
                      >
                        <i className="bi bi-arrow-clockwise fs-5 me-2" />
                        RENEW
                      </Button>
                    ) : (
                      <>
                        <>
                          <Button
                            variant="outline-primary me-2"
                            size="xs"
                            onClick={handleShowRenew}
                          >
                            <i className="bi bi-arrow-clockwise fs-5 me-2" />
                            RENEW
                          </Button>
                          <Modal
                            show={showRenew}
                            onHide={handleCloseRenew}
                            backdrop="static"
                            size="lg"
                          >
                            <Modal.Header closeButton>
                              <Modal.Title>Renew Policy</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                              <Form>
                                <Row className="mb-3">
                                  <Form.Group
                                    as={Col}
                                    controlId="formGridPremium"
                                  >
                                    <Form.Label className="text-primary">
                                      Premium
                                    </Form.Label>
                                    <InputGroup className="mb-3">
                                      <InputGroup.Text>
                                        {policy?.policyCurrency || "GHS"}
                                      </InputGroup.Text>
                                      <Form.Control
                                        aria-label="Amount (to the nearest value)"
                                        required
                                        ref={premiumRef}
                                        type="number"
                                        value={renewPolicy?.premium || 0.0}
                                        onChange={(e) =>
                                          setRenewPolicy({
                                            ...renewPolicy,
                                            premium: e.target.value,
                                          })
                                        }
                                        onFocus={() =>
                                          premiumRef?.current.select()
                                        }
                                      />
                                      <InputGroup.Text className="fw-bold">
                                        {Number.parseFloat(
                                          renewPolicy?.premium
                                        ).toLocaleString("en-US")}
                                      </InputGroup.Text>
                                    </InputGroup>
                                  </Form.Group>
                                  <Form.Group
                                    as={Col}
                                    controlId="formGridLastPremium"
                                  >
                                    <Form.Label className="text-primary">
                                      Last Premium
                                    </Form.Label>
                                    <Form.Control
                                      disabled
                                      // eslint-disable-next-line no-new-wrappers
                                      defaultValue={new Number(
                                        policy?.premium
                                      ).toLocaleString("en-US")}
                                    />
                                  </Form.Group>
                                </Row>
                                <Row className="mb-3">
                                  <Form.Group
                                    as={Col}
                                    controlId="formGridCompany"
                                  >
                                    <Form.Label className="text-primary">
                                      Underwriting Company
                                    </Form.Label>
                                    <Select
                                      ref={companyRef}
                                      id="company"
                                      options={
                                        dropDowns && dropDowns["company"]
                                          ? dropDowns["company"]
                                          : []
                                      }
                                      onChange={(selectedOption) =>
                                        setRenewPolicy({
                                          ...renewPolicy,
                                          CompanyId: selectedOption,
                                          BranchId: "",
                                        })
                                      }
                                      value={renewPolicy?.CompanyId || ""}
                                    />
                                  </Form.Group>
                                </Row>
                                <Row className="mb-3">
                                  <Form.Group
                                    as={Col}
                                    controlId="formGridBranch"
                                  >
                                    <Form.Label className="text-primary">
                                      Underwriting Branch
                                    </Form.Label>
                                    <Select
                                      ref={branchRef}
                                      id="branch"
                                      options={
                                        dropDowns && dropDowns["branch"]
                                          ? dropDowns["branch"]
                                          : []
                                      }
                                      onChange={(selectedOption) =>
                                        setRenewPolicy({
                                          ...renewPolicy,
                                          BranchId: selectedOption,
                                        })
                                      }
                                      value={renewPolicy?.BranchId || ""}
                                    />
                                  </Form.Group>
                                  <Form.Group
                                    as={Col}
                                    controlId="formGridPolicynumber"
                                  >
                                    <Form.Label className="text-primary">
                                      Policy Number
                                    </Form.Label>
                                    <Form.Control
                                      onChange={(e) =>
                                        setRenewPolicy({
                                          ...renewPolicy,
                                          policyNumber: e.target.value,
                                        })
                                      }
                                      value={renewPolicy?.policyNumber || ""}
                                    />
                                  </Form.Group>
                                </Row>
                                <Row className="mb-3">
                                  <Form.Group
                                    as={Col}
                                    controlId="formGridDuration"
                                  >
                                    <Form.Label className="text-primary">
                                      Duration (In months)
                                    </Form.Label>
                                    <Form.Control
                                      type="number"
                                      min={1}
                                      value={renewPolicy?.duration}
                                      onChange={(e) =>
                                        setRenewPolicy({
                                          ...renewPolicy,
                                          duration: e.target.value,
                                        })
                                      }
                                    />
                                  </Form.Group>

                                  <Form.Group
                                    as={Col}
                                    controlId="formGridEffectiveDate"
                                  >
                                    <Form.Label className="text-primary">
                                      Effective Date
                                    </Form.Label>
                                    <DatePicker
                                      dateFormat="dd/MM/yyyy"
                                      showMonthDropdown
                                      showPopperArrow={false}
                                      id="renew-effective-date"
                                      className="form-control"
                                      minDate={new Date(policy?.expires)}
                                      selected={
                                        new Date(
                                          renewPolicy?.effectiveDate ||
                                            startDate
                                        )
                                      }
                                      onChange={(date) =>
                                        setRenewPolicy({
                                          ...renewPolicy,
                                          effectiveDate: date,
                                        })
                                      }
                                      isClearable
                                    />
                                  </Form.Group>

                                  <Form.Group
                                    as={Col}
                                    controlId="formGridExpires"
                                  >
                                    <Form.Label className="text-primary">
                                      Expires
                                    </Form.Label>
                                    <Form.Control
                                      disabled
                                      onChange={(e) => {}}
                                      value={
                                        moment(renewPolicy?.expires).format(
                                          "DD/MM/YYYY"
                                        ) || ""
                                      }
                                    />
                                  </Form.Group>
                                </Row>
                                <Form.Group
                                  className="mb-3"
                                  controlId="formGridAddress2"
                                >
                                  <Form.Label className="text-primary">
                                    Remarks
                                  </Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    placeholder="Leave a comment here"
                                    style={{ height: "100px" }}
                                  />
                                </Form.Group>
                              </Form>
                            </Modal.Body>
                            <Modal.Footer>
                              <Button
                                variant="secondary"
                                onClick={handleCloseRenew}
                              >
                                Close
                              </Button>
                              <Button
                                variant="primary"
                                onClick={handleSaveRenewal}
                              >
                                Submit
                              </Button>
                            </Modal.Footer>
                          </Modal>
                        </>
                        <ClientPolicyProcessing policy={policy} />
                      </>
                    )}
                  </>
                </>
              )}
              {["active", "expiring"].includes(policy?.status) && (
                <>
                  <Button
                    variant="warning me-2"
                    size="xs"
                    onClick={() =>
                      navigate(`/claims/add/${policy?.ClientPolicyId}`)
                    }
                  >
                    <i className="bi bi-exclamation-circle fs-5 me-2" />
                    Log Claim
                  </Button>
                  <ClientPolicyAttachmentForm policy={policy} />
                </>
              )}
              <div className="d-flex align-items-center gap-1 text-nowrap ms-auto mb-3">
                <NavigateBackButton />
              </div>
            </div>
          )}
          <Accordion defaultActiveKey="1" className="mb-2">
            <Accordion.Item eventKey="0" className="mb-2">
              <Accordion.Header>Attachments</Accordion.Header>
              <Accordion.Body>
                <ClientPolicyAttachment policy={policy} />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Policy Requirements</Accordion.Header>
              <Accordion.Body>
                <ClientPolicyRequirement policy={policy} />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          <PolicyUnderwriteForm policy={policy} />
        </Tab>
        <Tab eventKey="claim" title="CLAIM VIEW"></Tab>
      </Tabs>
    </section>
  );
};

export default PolicyItemView;
